//app/register/owner.tsx
import {
  ScrollView,
  StyleSheet,
  Dimensions,
  View,
  Platform,
  KeyboardAvoidingView,
  AccessibilityInfo,
  TextInput as RNTextInput,
} from 'react-native';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TextInput,
  Button,
  Surface,
  Text,
  useTheme,
  Appbar,
  Portal,
  Dialog,
  ProgressBar,
  IconButton,
} from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { StackNavigationProp } from '@react-navigation/stack';
import Constants from 'expo-constants';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { CountryCodeSelector } from '../../src/components/CountryCodeSelector';
import OperatorLicenseInput from '../../src/components/OperatorLicenseInput';
import {
  checkEmailAvailable,
  checkNifAvailable,
  checkNipcAvailable,
  registerSupportAccount,
  requestOwnerSupport,
} from '../../src/api/apiService';
import { track, resolveEnvironment } from '../../src/lib/analytics';
import { AnalyticsEvent } from '../../src/lib/analyticsEvents';
import ChatBubble from '@/src/components/chat/ChatBubble';

import { OwnerOnboardingChatView } from '@/src/features/onboarding/useOwnerOnboardingChat';
import TypingIndicator from '@/src/components/chat/TypingIndicator';
import ChatConversation from '@/src/components/chat/ChatConversation';
import { useAuth } from '@/src/context/AuthContext';
import { ReferralCodeField } from '@/src/components/referrals/ReferralCodeField';
import { isRegistrationSessionRejection, registrationReferralPayload } from '@/src/components/referrals/registrationReferral';
import { beginRegistrationSession } from '@/src/components/referrals/registrationSession';
import { shareLinkCapturedAt, shareLinkReferralCode } from '@/src/utils/shareLinkReferral';
import { clearPendingReferral } from '@/src/utils/pendingReferral';
import { RegistrationAppStoreLinks } from '@/src/components/appStores/AppStoreLinks';
import {
  clearOwnerDriverStorage,
  setOwnerDriverIntent,
} from '@/src/features/owner/ownerDriverPrompt';
import {
  classifyOwnerRegistrationPrecheck,
  getOwnerRegistrationActionLabelKey,
  getOwnerRegistrationConflictActions,
  type OwnerRegistrationAction,
} from '@/src/features/owner/ownerRegistrationConflict';
import { buildOwnerRegistrationPayload } from '@/src/features/onboarding/ownerRegistrationPayload';

// Typed wrapper giving access to react-native-web's dataSet prop without
// falling back to @ts-ignore. The prop maps { key: value } → data-key="value"
// in the DOM; it is a no-op on native.
type DataSetProps = { dataSet?: Record<string, string | number | null | undefined> };
const DataSetView = View as React.ComponentType<
  React.ComponentPropsWithoutRef<typeof View> & DataSetProps
>;

type RootStackParamList = {
  Login: undefined;
  RegisterChoice: undefined;
  RegisterOwner: undefined;
  RegisterDriver: undefined;
  RegistrationSuccess: undefined;
};

type RegisterOwnerNavigationProp = StackNavigationProp<
  RootStackParamList,
  'RegisterOwner'
>;

type Props = {
  navigation: RegisterOwnerNavigationProp;
};

function SupportAccountRegistration({ supportToken }: { supportToken: string }) {
  const { t } = useTranslation('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!email || !password || !fullName || !phone) {
      setMessage(t('supportAccountRequired'));
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      const response = await registerSupportAccount({
        support_token: supportToken,
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        phone_number: phone.trim(),
      });
      setMessage(
        response.ok
          ? t('supportAccountCreated')
          : (response.data as any)?.detail || t('supportAccountFailed'),
      );
    } catch {
      setMessage(t('supportAccountFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Surface style={styles.container}>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('supportAccountTitle')} />
      </Appbar.Header>
      <ScrollView contentContainerStyle={styles.supportAccountContent}>
        <Text variant="bodyLarge" style={{ marginBottom: 16 }}>
          {t('supportAccountIntro')}
        </Text>
        <TextInput
          mode="outlined"
          label={t('supportAccountName')}
          value={fullName}
          onChangeText={setFullName}
          disabled={submitting}
          testID="support-account-name"
          style={{ marginBottom: 10 }}
        />
        <TextInput
          mode="outlined"
          label={t('supportRequestEmail')}
          value={email}
          onChangeText={setEmail}
          disabled={submitting}
          autoCapitalize="none"
          keyboardType="email-address"
          testID="support-account-email"
          style={{ marginBottom: 10 }}
        />
        <TextInput
          mode="outlined"
          label={t('supportAccountPassword')}
          value={password}
          onChangeText={setPassword}
          disabled={submitting}
          secureTextEntry
          testID="support-account-password"
          style={{ marginBottom: 10 }}
        />
        <TextInput
          mode="outlined"
          label={t('supportRequestPhone')}
          value={phone}
          onChangeText={setPhone}
          disabled={submitting}
          keyboardType="phone-pad"
          testID="support-account-phone"
          style={{ marginBottom: 10 }}
        />
        {!!message && <Text accessibilityRole="alert" style={{ marginBottom: 10 }}>{message}</Text>}
        <Button
          mode="contained"
          onPress={submit}
          loading={submitting}
          disabled={submitting}
          testID="support-account-submit"
        >
          {t('supportAccountSubmit')}
        </Button>
      </ScrollView>
    </Surface>
  );
}

const { width: screenWidth } = Dimensions.get('window');

// Helper functions for masking/unmasking
const removeMask = (value: string): string => value.replace(/\D/g, '');

const applyMaskNipc = (value: string): string =>
  value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
    .substring(0, 11);

// License masking now handled by reusable component

/**
 * Chat-flow registration (M13 locked decision: "chat-flow uma pergunta por
 * tela"). One question per screen with a progress bar — matches the
 * onboarding fleet-size/licenses screens so the whole owner funnel feels
 * like a single conversation.
 */
type ChatStepId =
  | 'email'
  | 'password'
  | 'name'
  | 'phone'
  | 'ownerType'
  | 'alsoDriver'
  | 'nif'
  | 'companyName'
  | 'companyNipc'
  | 'license';

// alsoDriver comes last in both flows — after the NIF (individual) or the
// operator license (company), so the owner has already established their
// operating identity before being asked about their own driving.
const COMMON_STEPS: ChatStepId[] = ['email', 'password', 'name', 'phone', 'ownerType'];
const INDIVIDUAL_STEPS: ChatStepId[] = [...COMMON_STEPS, 'nif', 'alsoDriver'];
const COMPANY_STEPS: ChatStepId[] = [...COMMON_STEPS, 'companyName', 'companyNipc', 'license', 'alsoDriver'];

// Which form fields belong to each chat step (drives per-step validation and
// "jump to the step that has the server error").
const STEP_FIELDS: Record<ChatStepId, string[]> = {
  email: ['email'],
  password: ['password', 'confirmPassword'],
  name: ['full_name'],
  phone: ['phoneNumber'],
  ownerType: ['ownerType'],
  alsoDriver: ['ownerAlsoDriver'],
  nif: ['ownerNif'],
  companyName: ['companyName'],
  companyNipc: ['companyNipc'],
  license: ['numeroLicenca'],
};

export default function RegisterOwner({ navigation }: Props) {
  const { t, i18n: i18nInstance } = useTranslation('register');
  const { authenticateWithToken } = useAuth();
  const theme = useTheme();
  const params = useLocalSearchParams<{ support_token?: string; ref?: string | string[]; ref_at?: string | string[] }>();
  const supportToken = typeof params.support_token === 'string' ? params.support_token : undefined;
  // D-057: a referral link uses ordinary signup. The code section exists only
  // for share-link visitors on web and optional manual entry on native.
  const initialReferral = shareLinkReferralCode(params.ref);
  const initialReferralAt = initialReferral ? shareLinkCapturedAt(params.ref_at) : undefined;
  // D-057: the registration session token is SERVER-issued. It is requested
  // once, as soon as the referral step becomes part of the flow, and never
  // invented client-side. `null` means the endpoint is unreachable: the field
  // reports a transient state requiring retry or explicit unattributed continuation.
  const [referralToken, setReferralToken] = useState<string | null>(null);
  const referralStateRef = useRef<{ kind: string; code?: string }>({ kind: initialReferral ? 'pending' : 'idle' });
  const referralDraftRef = useRef(initialReferral);
  const [referralRejection, setReferralRejection] = useState('');
  const [referralRecapture, setReferralRecapture] = useState(0);
  const referralActive = !supportToken && (!!initialReferral || Platform.OS !== 'web');
  useEffect(() => {
    if (!referralActive) return;
    let current = true;
    void beginRegistrationSession().then(token => { if (current) setReferralToken(token); });
    return () => { current = false; };
  }, [referralActive]);
  const [countryCode, setCountryCode] = useState('+351'); // Default to Portugal
  const [menuVisible, setMenuVisible] = useState(false);
  const [serverError, setServerError] = useState<{
    numero_licenca?: string;
    nipc?: string;
    certidao_permanente?: string;
    nif?: string;
    name?: string;
    email?: string;
    password?: string;
    full_name?: string;
    phone_number?: string;
  } | null>(null);
  const [stepId, setStepId] = useState<ChatStepId>('email');
  // M13 point-2: once the account is created we DON'T navigate to a fresh
  // onboarding route (which restarted the chat and dropped the history).
  // Instead we flip to the 'onboarding' phase and continue the CRM → product →
  // licenses → trial steps inline, in the same ChatConversation.
  const [phase, setPhase] = useState<'register' | 'onboarding'>('register');
  const [checking, setChecking] = useState(false);
  // Conversation beat: brief typing dots before the next question appears.
  const [typingBeat, setTypingBeat] = useState(false);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether at least one beat has fired, so focus/announcement effects
  // are silent on initial mount.
  const hasBeatRef = useRef(false);
  const advanceTo = (next: ChatStepId) => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    hasBeatRef.current = true;
    setTypingBeat(true);
    setStepId(next);
    typingTimerRef.current = setTimeout(() => {
      typingTimerRef.current = null;
      setTypingBeat(false);
    }, 650);
  };
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // The password step is the only one with two inputs: Enter/Tab on the
  // password must land on the confirmation, not submit the step.
  const confirmPasswordRef = useRef<RNTextInput>(null);
  // Duplicate-identity (409) dialog — Alert.alert with buttons is a no-op on
  // web, so the "log in" CTA must live in a Dialog (same pattern as
  // register/driver.tsx).
  const [dupDialogVisible, setDupDialogVisible] = useState(false);
  const [dupDialogMessage, setDupDialogMessage] = useState('');
  const [dupDialogEmail, setDupDialogEmail] = useState('');
  const [dupDialogActions, setDupDialogActions] = useState<OwnerRegistrationAction[]>([
    'login',
    'recover_account',
  ]);
  const [supportDialogVisible, setSupportDialogVisible] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [supportDescription, setSupportDescription] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState('');
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  const { API_BASE_URL } =
    (Constants.expoConfig?.extra as { API_BASE_URL: string }) || {};

  useEffect(() => {
    // Top-of-funnel: user landed on the owner registration form (#323).
    track(AnalyticsEvent.SignupStarted, {
      user_role: 'owner',
      entry_point: 'register_owner',
    });
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  // After the typing beat the active question appears. Restore orientation for
  // keyboard and AT users: web restores focus to the new input; iOS announces
  // the question via VoiceOver (live regions are unsupported on iOS; Android
  // and web receive the announcement via accessibilityLiveRegion on the bubble).
  useEffect(() => {
    if (!hasBeatRef.current || typingBeat) return;

    if (Platform.OS === 'ios') {
      // questionFor is evaluated from t() on every render but is textually
      // stable; omitting it from deps avoids re-firing on unrelated re-renders.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const question = questionFor[stepId]?.question;
      if (question) AccessibilityInfo.announceForAccessibility(question);
    }

    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-testid="chat-input-${stepId}"]`,
      ) as HTMLElement | null;
      el?.focus();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingBeat, stepId]);

  // Create the schema inside the component so that we can use t(…) for error messages.
  const registerOwnerSchema = z.object({
    email: z
      .string()
      .min(1, { message: t('emailRequired') })
      .email({ message: t('invalidEmail') }),
    password: z
      .string()
      .min(1, { message: t('passwordRequired') })
      .min(8, { message: t('passwordTooShort') }),
    confirmPassword: z
      .string()
      .min(1, { message: t('confirmPasswordRequired') }),
    full_name: z.string().min(1, { message: t('fullNameRequired') }),
    phoneNumber: z
      .string()
      .min(1, { message: t('phoneNumberRequired') })
      .min(8, { message: t('phoneNumberInvalid') })
      .max(15, { message: t('phoneNumberTooLong') })
      .refine(val => /^[\d\s-]+$/.test(val), { message: t('phoneNumberInvalid') }), // Local digits only (country code added separately)
    ownerType: z.enum(['individual', 'company']),
    ownerAlsoDriver: z.enum(['yes', 'no']).optional(),
    ownerNif: z.string().optional(),
    companyName: z.string().optional(),
    companyNipc: z.string().optional(),
    numeroLicenca: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: 'custom', path: ['confirmPassword'], message: t('passwordsDoNotMatch') });
    }
    if (data.ownerType === 'individual') {
      if (!data.ownerNif || removeMask(data.ownerNif).length !== 9) {
        ctx.addIssue({ code: 'custom', path: ['ownerNif'], message: t('nifInvalidLength') });
      }
      return;
    }
    if (!data.companyName) {
      ctx.addIssue({ code: 'custom', path: ['companyName'], message: t('companyNameRequired') });
    }
    if (!data.companyNipc || removeMask(data.companyNipc).length !== 9) {
      ctx.addIssue({ code: 'custom', path: ['companyNipc'], message: t('nipcInvalidLength') });
    }
    if (!data.numeroLicenca || !/^\d{1,10}\/\d{4}$/.test(data.numeroLicenca)) {
      ctx.addIssue({ code: 'custom', path: ['numeroLicenca'], message: t('numeroLicencaOperadorInvalidFormat') });
    }
  });

  type RegisterOwnerFormData = z.infer<typeof registerOwnerSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<RegisterOwnerFormData>({
    resolver: zodResolver(registerOwnerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      full_name: '',
      phoneNumber: '',
      // Default stays 'company' until the individual (company-less) owner has
      // a working post-registration experience — vehicles/drivers still
      // require a Company (follow-up in #208/#209).
      ownerType: 'company',
      ownerAlsoDriver: undefined,
      ownerNif: '',
      companyName: '',
      companyNipc: '',
      numeroLicenca: '',
    },
  });

  const ownerType = watch('ownerType');

  const stepOrder: ChatStepId[] = ownerType === 'individual' ? INDIVIDUAL_STEPS : COMPANY_STEPS;
  const stepIndex = stepOrder.indexOf(stepId);
  const totalSteps = stepOrder.length;
  const isLastStep = stepIndex === totalSteps - 1;

  // Per-step validation: reuse the exact field rules from the full schema so
  // the chat flow can never diverge from what the final submit validates.
  const validateStep = async (): Promise<boolean> => {
    const data = control._formValues as RegisterOwnerFormData;
    let stepErrors: Record<string, string> = {};

    const addIssue = (field: string, message: string) => {
      stepErrors[field] = message;
    };

    switch (stepId) {
      case 'email': {
        const res = z.string().min(1, { message: t('emailRequired') }).email({ message: t('invalidEmail') }).safeParse(data.email);
        if (!res.success) addIssue('email', res.error.issues[0].message);
        break;
      }
      case 'password': {
        if (!data.password) addIssue('password', t('passwordRequired'));
        else if (data.password.length < 8) addIssue('password', t('passwordTooShort'));
        if (!data.confirmPassword) addIssue('confirmPassword', t('confirmPasswordRequired'));
        else if (data.password !== data.confirmPassword) addIssue('confirmPassword', t('passwordsDoNotMatch'));
        break;
      }
      case 'name': {
        if (!data.full_name) addIssue('full_name', t('fullNameRequired'));
        break;
      }
      case 'phone': {
        const res = z
          .string()
          .min(1, { message: t('phoneNumberRequired') })
          .min(8, { message: t('phoneNumberInvalid') })
          .max(15, { message: t('phoneNumberTooLong') })
          .refine(val => /^[\d\s-]+$/.test(val), { message: t('phoneNumberInvalid') })
          .safeParse(data.phoneNumber);
        if (!res.success) addIssue('phoneNumber', res.error.issues[0].message);
        break;
      }
      case 'ownerType':
        break;
      case 'alsoDriver':
        break;
      case 'nif': {
        if (!data.ownerNif || removeMask(data.ownerNif).length !== 9) addIssue('ownerNif', t('nifInvalidLength'));
        break;
      }
      case 'companyName': {
        if (!data.companyName) addIssue('companyName', t('companyNameRequired'));
        break;
      }
      case 'companyNipc': {
        if (!data.companyNipc || removeMask(data.companyNipc).length !== 9) addIssue('companyNipc', t('nipcInvalidLength'));
        break;
      }
      case 'license': {
        if (!data.numeroLicenca || !/^\d{1,10}\/\d{4}$/.test(data.numeroLicenca)) {
          addIssue('numeroLicenca', t('numeroLicencaOperadorInvalidFormat'));
        }
        break;
      }
    }

    Object.entries(stepErrors).forEach(([field, message]) => {
      setError(field as any, { type: 'manual', message });
    });
    return Object.keys(stepErrors).length === 0;
  };

  // Jump to the first chat step that carries one of the given error fields.
  const goToStepWithField = (fields: string[]) => {
    for (const step of stepOrder) {
      if (STEP_FIELDS[step].some(f => fields.includes(f))) {
        setStepId(step);
        return;
      }
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepId(stepOrder[stepIndex - 1]);
    } else {
      router.back();
    }
  };

  const showDuplicateIdentityDialog = (
    conflict: { detail?: string; action?: string; actions?: string[] },
    email: string,
  ) => {
    setDupDialogMessage(conflict.detail || t('duplicateIdentityMessage'));
    setDupDialogEmail(email);
    setDupDialogActions(getOwnerRegistrationConflictActions(conflict));
    setDupDialogVisible(true);
  };

  const handleDuplicateIdentityAction = (action: OwnerRegistrationAction) => {
    setDupDialogVisible(false);
    if (action === 'login') {
      router.push({ pathname: '/', params: { prefill_email: dupDialogEmail } });
      return;
    }
    if (action === 'recover_account') {
      router.push({
        pathname: '/',
        params: { prefill_email: dupDialogEmail, open_recovery: 'true' },
      });
      return;
    }

    const currentData = control._formValues as RegisterOwnerFormData;
    setSupportEmail(dupDialogEmail);
    setSupportPhone(`${countryCode}${currentData.phoneNumber || ''}`);
    setSupportDescription('');
    setSupportMessage('');
    setSupportDialogVisible(true);
  };

  const handleSupportSubmit = async () => {
    const currentData = control._formValues as RegisterOwnerFormData;
    const email = supportEmail.trim();
    const phone = supportPhone.trim();
    const description = supportDescription.trim();
    if (!email) {
      setSupportMessage(t('supportRequestEmailRequired'));
      return;
    }
    if (!phone) {
      setSupportMessage(t('supportRequestPhoneRequired'));
      return;
    }
    if (!description) {
      setSupportMessage(t('supportRequestDescriptionRequired'));
      return;
    }

    setSupportSubmitting(true);
    setSupportMessage('');
    try {
      const response = await requestOwnerSupport({
        tax_identifier: removeMask(currentData.companyNipc || ''),
        email,
        phone,
        description,
      });
      setSupportMessage(
        response.ok
          ? (response.data.detail || t('supportRequestReceived'))
          : t('supportRequestFailed'),
      );
    } catch {
      setSupportMessage(t('supportRequestFailed'));
    } finally {
      setSupportSubmitting(false);
    }
  };

  const showErrorDialog = (title: string, message: string) => {
    setErrorDialogTitle(title);
    setErrorDialogMessage(message);
    setErrorDialogVisible(true);
  };

  const handleContinue = async () => {
    const isValid = await validateStep();
    if (!isValid) return;

    const data = control._formValues as RegisterOwnerFormData;

    // Server-side availability checks live on their own step so the user
    // gets the feedback right where they typed the value.
    if (stepId === 'email') {
      if (referralActive && !registrationReferralPayload(referralStateRef.current, referralToken)) {
        showErrorDialog(t('error'), t('partner:codeFieldResolve'));
        return;
      }
      setChecking(true);
      try {
        const res = await checkEmailAvailable(data.email);
        if (!res.ok) {
          const errData: any = res.data as any;
          showErrorDialog(t('error'), errData?.detail || errData?.message || t('unexpectedError'));
          return;
        }
        if (!res.data.available) {
          setError('email' as any, { type: 'server', message: res.data.reason || t('invalidEmail') });
          return;
        }
      } finally {
        setChecking(false);
      }
    }

    if (stepId === 'nif') {
      setChecking(true);
      try {
        const nifCheck = await checkNifAvailable(removeMask(data.ownerNif || ''));
        if (!nifCheck.ok) {
          const errData: any = nifCheck.data as any;
          showErrorDialog(t('error'), errData?.detail || errData?.message || t('unexpectedError'));
          return;
        }
        if (!nifCheck.data.available) {
          setError('ownerNif' as any, { type: 'server', message: nifCheck.data.reason || t('nifInvalid') });
          return;
        }
      } finally {
        setChecking(false);
      }
    }

    if (stepId === 'companyNipc') {
      setChecking(true);
      try {
        const nipcCheck = await checkNipcAvailable(removeMask(data.companyNipc || ''));
        if (!nipcCheck.ok) {
          const errData: any = nipcCheck.data as any;
          showErrorDialog(t('error'), errData?.detail || errData?.message || t('unexpectedError'));
          return;
        }
        if (classifyOwnerRegistrationPrecheck(nipcCheck.data) === 'invalid') {
          setError('companyNipc' as any, {
            type: 'server',
            message: nipcCheck.data.reason || t('nipcInvalid'),
          });
          return;
        }
        if (!nipcCheck.data.available) {
          showDuplicateIdentityDialog(
            {
              detail: nipcCheck.data.reason,
              action: nipcCheck.data.action,
              actions: nipcCheck.data.actions,
            },
            data.email,
          );
          return;
        }
      } finally {
        setChecking(false);
      }
    }

    if (isLastStep) {
      // If the full-schema resolver rejects a field from an earlier step,
      // jump back to the step that owns it — otherwise the failure would be
      // invisible on the current screen.
      handleSubmit(handleRegister, (formErrors) => {
        goToStepWithField(Object.keys(formErrors));
      })();
      return;
    }
    advanceTo(stepOrder[stepIndex + 1]);
  };

  const selectOwnerType = (type: 'individual' | 'company') => {
    setValue('ownerType', type, { shouldValidate: false });
    clearErrors(['ownerNif', 'companyName', 'companyNipc', 'numeroLicenca'] as any);
    // Selecting the persona answers the question — advance to the next
    // identity question (NIF or company details).
    advanceTo(type === 'individual' ? 'nif' : 'companyName');
  };

  const selectAlsoDriver = (intent: 'yes' | 'no') => {
    setValue('ownerAlsoDriver', intent, { shouldValidate: false });
    // alsoDriver is the last step — selecting an option submits the
    // registration (a share-link code is entered on the email step).
    void handleContinue();
  };

  // Add helper to map server errors to form fields
  const handleFieldErrors = (errorsFromServer: Record<string, any>) => {
    const erroredFields: string[] = [];
    Object.entries(errorsFromServer).forEach(([key, value]) => {
      if (key === 'company' && typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([subKey, subVal]) => {
          const message = Array.isArray(subVal) ? subVal[0] : subVal;
          let formFieldName: keyof RegisterOwnerFormData;
          switch (subKey) {
            case 'name':
              formFieldName = 'companyName';
              break;
            case 'nipc':
              formFieldName = 'companyNipc';
              break;
            case 'numero_licenca':
              formFieldName = 'numeroLicenca';
              break;
            default:
              return;
          }
          erroredFields.push(formFieldName);
          setError(formFieldName, { type: 'server', message });
        });
      } else {
        const message = Array.isArray(value) ? value[0] : value;
        let formFieldName: keyof RegisterOwnerFormData;
        switch (key) {
          case 'email':
            formFieldName = 'email';
            break;
          case 'password':
            formFieldName = 'password';
            break;
          case 'confirmPassword':
            formFieldName = 'confirmPassword';
            break;
          case 'full_name':
            formFieldName = 'full_name';
            break;
          case 'phone_number':
            formFieldName = 'phoneNumber';
            break;
          case 'nif':
            formFieldName = 'ownerNif';
            break;
          default:
            return;
        }
        erroredFields.push(formFieldName);
        setError(formFieldName, { type: 'server', message });
      }
    });
    if (erroredFields.length > 0) {
      goToStepWithField(erroredFields);
    }
  };

  const handleRegister: SubmitHandler<RegisterOwnerFormData> = async (data) => {
    try {
      // clear any previous server error state
      setServerError(null);
      let payload: Record<string, unknown>;

      if (data.ownerType === 'individual') {
        const nifRaw = removeMask(data.ownerNif || '');
        const nifCheck = await checkNifAvailable(nifRaw);
        if (!nifCheck.ok) {
          const errData: any = nifCheck.data as any;
          showErrorDialog(t('error'), errData?.detail || errData?.message || t('unexpectedError'));
          return;
        }
        if (!nifCheck.data.available) {
          setStepId('nif');
          setError('ownerNif' as any, { type: 'server', message: nifCheck.data.reason || t('nifInvalid') });
          return;
        }
      } else {
        // Server-side pre-checks (NIPC availability)
        const nipcRaw = removeMask(data.companyNipc || '');
        const nipcCheck = await checkNipcAvailable(nipcRaw);
        if (!nipcCheck.ok) {
          const errData: any = nipcCheck.data as any;
          showErrorDialog(t('error'), errData?.detail || errData?.message || t('unexpectedError'));
          return;
        }
        if (classifyOwnerRegistrationPrecheck(nipcCheck.data) === 'invalid') {
          setStepId('companyNipc');
          setError('companyNipc' as any, {
            type: 'server',
            message: nipcCheck.data.reason || t('nipcInvalid'),
          });
          return;
        }
        if (!nipcCheck.data.available) {
          showDuplicateIdentityDialog(
            {
              detail: nipcCheck.data.reason,
              action: nipcCheck.data.action,
              actions: nipcCheck.data.actions,
            },
            data.email,
          );
          return;
        }
      }

      // D-057: the referral code and its SERVER-issued registration session are
      // carried IN the signup request itself, so attribution freezes atomically
      // with account creation. There is no post-signup claim.
      const referralPayload = registrationReferralPayload(referralStateRef.current, referralToken);
      if (referralActive && !referralPayload) {
        setStepId('email');
        showErrorDialog(t('error'), t('partner:codeFieldResolve'));
        return;
      }
      payload = buildOwnerRegistrationPayload({
        email: data.email,
        password: data.password,
        full_name: data.full_name,
        phone_number: `${countryCode}${data.phoneNumber}`,
        language: i18nInstance.language,
        owner_type: data.ownerType,
        also_driver_intent: data.ownerAlsoDriver ?? null,
        ownerNif: data.ownerNif,
        companyName: data.companyName,
        companyNipc: data.companyNipc,
        numeroLicenca: data.numeroLicenca,
      });
      if (referralActive) payload = { ...payload, ...referralPayload };

      const response = await fetch(
        `${API_BASE_URL}/api/users/register-owner/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData?.referral_code && isRegistrationSessionRejection(errorData)) {
          // F10: the session expired, not the code. A fresh session makes the
          // field re-capture the accepted code before the next submission.
          setReferralToken(await beginRegistrationSession());
          setReferralRecapture(n => n + 1);
          setStepId('email');
          showErrorDialog(t('error'), t('partner:codeFieldSessionExpired'));
          return;
        }
        if (errorData?.referral_code) {
          referralStateRef.current = { kind: 'invalid' };
          const detail = Array.isArray(errorData.referral_code) ? errorData.referral_code[0] : errorData.referral_code;
          const message = typeof detail === 'string' ? detail : t('partner:codeFieldResolve');
          setReferralRejection(message);
          setStepId('email');
          showErrorDialog(t('error'), message);
          return;
        }
        if (response.status === 409 && errorData?.code === 'duplicate_identity') {
          // Expected conflict: preserve every server-provided recovery action
          // instead of collapsing the response to a generic field error.
          showDuplicateIdentityDialog(errorData, data.email);
          return;
        }
        // Map any field-level errors (phone_number, email, etc.) to form fields
        const hasFieldErrors = Object.keys(errorData).some(
          k => k !== 'detail' && k !== 'company' && !k.startsWith('_')
        );
        if (errorData.company || hasFieldErrors) {
          // map server errors to form fields and jump to the step that owns them
          handleFieldErrors(errorData);
        } else if (errorData.detail) {
          showErrorDialog(t('registerFailed'), errorData.detail);
        } else {
          showErrorDialog(t('registerFailed'), t('unexpectedError'));
        }
      } else {
        track(AnalyticsEvent.SignupCompleted, { user_role: 'owner', auth_method: 'email', app_variant: resolveEnvironment() });
        // ALL-22: the device's deferred referral was used (or not) by this signup.
        await clearPendingReferral();
        await clearOwnerDriverStorage();
        await setOwnerDriverIntent(
          data.ownerAlsoDriver === 'yes' ? 'yes' : 'no',
          data.email,
        );
        // M13 funnel B0: registration now auto-authenticates. Store the session
        // and continue straight into the owner funnel (CRM → licenses → paywall)
        // instead of bouncing the brand-new owner to the login screen.
        const successData: any = await response.json().catch(() => ({}));
        const authToken: string | undefined = successData?.token;
        if (authToken) {
          await authenticateWithToken(authToken, data.email);
          // Continue in the SAME chat — no route hop, no restart (M13 point-2).
          setPhase('onboarding');
          track(AnalyticsEvent.OnboardingStarted, {
            user_role: 'owner',
            flow: 'owner_onboarding',
            app_variant: resolveEnvironment(),
          });
        } else {
          // Defensive fallback: account exists, so this is not a dead end.
          router.push('/register/success');
        }
      }
    } catch (error) {
      showErrorDialog(t('registerFailed'), t('unexpectedError'));
    }
  };

  const fieldError = (field: keyof RegisterOwnerFormData, serverKey?: keyof NonNullable<typeof serverError>) => {
    const server = serverKey ? serverError?.[serverKey] : undefined;
    const form = errors[field]?.message as string | undefined;
    if (!server && !form) return null;
    return (
      <Text style={{ color: theme.colors.error, marginTop: 5 }} accessibilityRole="alert">
        {server || form}
      </Text>
    );
  };

  const questionFor: Record<ChatStepId, { question: string; hint?: string }> = {
    email: {
      question: t('chatEmailQuestion'),
      hint: t('chatEmailHint'),
    },
    password: {
      question: t('chatPasswordQuestion'),
      hint: t('chatPasswordHint'),
    },
    name: { question: t('chatNameQuestion') },
    phone: {
      question: t('chatPhoneQuestion'),
      hint: t('chatPhoneHint'),
    },
    ownerType: {
      question: t('chatOwnerTypeQuestion'),
      hint: t('chatOwnerTypeHint'),
    },
    alsoDriver: {
      question: t('chatAlsoDriverQuestion'),
      hint: t('chatAlsoDriverHint'),
    },
    nif: { question: t('chatNifQuestion') },
    companyName: { question: t('chatCompanyNameQuestion') },
    companyNipc: { question: t('chatNipcQuestion') },
    license: {
      question: t('chatLicenseQuestion'),
      hint: t('chatLicenseHint'),
    },
  };

  // All step inputs stay MOUNTED (hidden with display:none when inactive):
  // react-hook-form drops unmounted Controller fields from the submit
  // payload, which silently failed the final validation with values the
  // user had already typed.
  const ALL_STEP_IDS: ChatStepId[] = [
    'email', 'password', 'name', 'phone', 'ownerType', 'alsoDriver', 'nif', 'companyName', 'companyNipc', 'license',
  ];

  // Enter (web) / the keyboard action key (native) answers the current
  // question — same effect as pressing "Continuar". Without this the chat
  // could only be advanced with the mouse.
  const submitOnEnter = {
    returnKeyType: (isLastStep ? 'done' : 'next') as 'done' | 'next',
    onSubmitEditing: () => {
      void handleContinue();
    },
  };

  // The show/hide toggle sits after its input in the DOM, so leaving it in the
  // tab sequence swallowed the Tab that should go password → confirmation.
  // It stays clickable/tappable and screen-reader reachable.
  const revealToggleProps = { focusable: false } as const;

  const renderStepInputFor = (renderId: ChatStepId) => {
    switch (renderId) {
      case 'email':
        return (
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('emailPlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  autoCapitalize="none"
                  autoFocus
                  keyboardType="email-address"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text.toLowerCase());
                    setServerError(prev => prev ? { ...prev, email: undefined } : null);
                  }}
                  value={value}
                  error={!!errors.email || !!serverError?.email}
                  style={styles.input}
                  testID="chat-input-email"
                  {...submitOnEnter}
                />
                {fieldError('email', 'email')}
                {referralActive && <ReferralCodeField
                  registrationToken={referralToken}
                  initialCode={referralDraftRef.current}
                  linkCapturedAt={initialReferralAt}
                  serverRejected={referralRejection}
                  recaptureSignal={referralRecapture}
                  onDraftChange={code => { referralDraftRef.current = code; setReferralRejection(''); }}
                  onRegistrationRetry={async () => {
                    const token = await beginRegistrationSession();
                    setReferralToken(token);
                    return token;
                  }}
                  onChange={next => { referralStateRef.current = next; }}
                />}
              </>
            )}
          />
        );
      case 'password':
        return (
          <>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <TextInput
                    label={t('passwordPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    onBlur={onBlur}
                    onChangeText={(text) => {
                      onChange(text);
                      setServerError(prev => prev ? { ...prev, password: undefined } : null);
                    }}
                    value={value}
                    error={!!errors.password || !!serverError?.password}
                    style={styles.input}
                    testID="chat-input-password"
                    returnKeyType="next"
                    blurOnSubmit={false}
                    onSubmitEditing={() => confirmPasswordRef.current?.focus()}
                    right={
                      <TextInput.Icon
                        icon={showPassword ? 'eye-off' : 'eye'}
                        disabled={typingBeat}
                        onPress={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        {...revealToggleProps}
                      />
                    }
                  />
                  {fieldError('password', 'password')}
                </>
              )}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <TextInput
                    ref={confirmPasswordRef}
                    label={t('confirmPasswordPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    secureTextEntry={!showConfirmPassword}
                    autoCapitalize="none"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    error={!!errors.confirmPassword}
                    style={styles.input}
                    testID="chat-input-confirm-password"
                    {...submitOnEnter}
                    right={
                      <TextInput.Icon
                        icon={showConfirmPassword ? 'eye-off' : 'eye'}
                        disabled={typingBeat}
                        onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                        {...revealToggleProps}
                      />
                    }
                  />
                  {fieldError('confirmPassword')}
                </>
              )}
            />
          </>
        );
      case 'name':
        return (
          <Controller
            control={control}
            name="full_name"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('fullNamePlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text);
                    setServerError(prev => prev ? { ...prev, full_name: undefined } : null);
                  }}
                  value={value}
                  error={!!errors.full_name || !!serverError?.full_name}
                  style={styles.input}
                  testID="chat-input-name"
                  {...submitOnEnter}
                />
                {fieldError('full_name', 'full_name')}
              </>
            )}
          />
        );
      case 'phone':
        return (
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <View style={styles.phoneInputContainer}>
                  <Button
                    mode="outlined"
                    disabled={typingBeat}
                    onPress={() => setMenuVisible(true)}
                    style={styles.countryCodeButton}
                  >
                    {countryCode}
                  </Button>
                  <TextInput
                    label={t('phoneNumberPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    keyboardType="phone-pad"
                    onBlur={onBlur}
                    onChangeText={(text) => {
                      onChange(text);
                      setServerError(prev => prev ? { ...prev, phone_number: undefined } : null);
                    }}
                    value={value}
                    error={!!errors.phoneNumber || !!serverError?.phone_number}
                    style={styles.phoneInput}
                    maxLength={15}
                    testID="chat-input-phone"
                    {...submitOnEnter}
                  />
                </View>
                <CountryCodeSelector
                  visible={menuVisible}
                  onDismiss={() => setMenuVisible(false)}
                  onSelect={setCountryCode}
                  selectedCode={countryCode}
                />
                {fieldError('phoneNumber', 'phone_number')}
              </>
            )}
          />
        );
      case 'ownerType':
        return (
          <View style={styles.ownerTypeOptions}>
            <Button
              mode={ownerType === 'company' ? 'contained' : 'outlined'}
              disabled={typingBeat}
              onPress={() => selectOwnerType('company')}
              style={styles.ownerTypeOption}
              contentStyle={styles.ownerTypeOptionContent}
              testID="chat-owner-type-company"
            >
              {t('ownerTypeCompany')}
            </Button>
            <Button
              mode={ownerType === 'individual' ? 'contained' : 'outlined'}
              disabled={typingBeat}
              onPress={() => selectOwnerType('individual')}
              style={styles.ownerTypeOption}
              contentStyle={styles.ownerTypeOptionContent}
              testID="chat-owner-type-individual"
            >
              {t('ownerTypeIndividual')}
            </Button>
          </View>
        );
      case 'alsoDriver':
        return (
          <View style={styles.ownerTypeOptions}>
            <Button
              mode="contained"
              disabled={typingBeat}
              onPress={() => selectAlsoDriver('yes')}
              style={styles.ownerTypeOption}
              contentStyle={styles.ownerTypeOptionContent}
              testID="chat-also-driver-yes"
            >
              {t('chatAlsoDriverYes')}
            </Button>
            <Button
              mode="outlined"
              disabled={typingBeat}
              onPress={() => selectAlsoDriver('no')}
              style={styles.ownerTypeOption}
              contentStyle={styles.ownerTypeOptionContent}
              testID="chat-also-driver-no"
            >
              {t('chatAlsoDriverNo')}
            </Button>
          </View>
        );

      case 'nif':
        return (
          <Controller
            control={control}
            name="ownerNif"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('nifLabel')}
                  mode="outlined"
                  disabled={typingBeat}
                  keyboardType="numeric"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(applyMaskNipc(text));
                    setServerError(prev => prev ? { ...prev, nif: undefined } : null);
                  }}
                  value={value}
                  error={!!errors.ownerNif || !!serverError?.nif}
                  style={styles.input}
                  testID="chat-input-nif"
                  {...submitOnEnter}
                />
                {fieldError('ownerNif', 'nif')}
              </>
            )}
          />
        );
      case 'companyName':
        return (
          <Controller
            control={control}
            name="companyName"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('companyNamePlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text);
                    setServerError(prev => prev ? { ...prev, name: undefined } : null);
                  }}
                  value={value || ''}
                  error={!!errors.companyName || !!serverError?.name}
                  style={styles.input}
                  testID="chat-input-company-name"
                  {...submitOnEnter}
                />
                {fieldError('companyName', 'name')}
              </>
            )}
          />
        );
      case 'companyNipc':
        return (
          <Controller
            control={control}
            name="companyNipc"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('nipcPlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  keyboardType="numeric"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(applyMaskNipc(text));
                    setServerError(prev => prev ? { ...prev, nipc: undefined } : null);
                  }}
                  value={value || ''}
                  error={!!errors.companyNipc || !!serverError?.nipc}
                  style={styles.input}
                  testID="chat-input-nipc"
                  {...submitOnEnter}
                />
                {fieldError('companyNipc', 'nipc')}
              </>
            )}
          />
        );
      case 'license':
        return (
          <Controller
            control={control}
            name="numeroLicenca"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                {typingBeat ? null : (
                  <OperatorLicenseInput
                    label={t('licenseNumberOperadorPlaceholder')}
                    value={value || ''}
                    onChangeText={(text) => {
                      onChange(text);
                      setServerError(prev => prev ? { ...prev, numero_licenca: undefined } : null);
                    }}
                    onBlur={onBlur}
                    error={!!errors.numeroLicenca || !!serverError?.numero_licenca}
                    style={styles.input}
                    testID="chat-input-license"
                    {...submitOnEnter}
                  />
                )}
                {fieldError('numeroLicenca', 'numero_licenca')}
              </>
            )}
          />
        );
    }
  };

  const { question, hint } = questionFor[stepId];

  // What the user "said" for a completed step — rendered as their bubble in
  // the conversation history. Password is never echoed.
  const answerSummaryFor = (id: ChatStepId): string => {
    const v = control._formValues as RegisterOwnerFormData;
    switch (id) {
      case 'email': return v.email;
      case 'password': return '••••••••';
      case 'name': return v.full_name;
      case 'phone': return `${countryCode} ${v.phoneNumber}`;
      case 'ownerType':
        return v.ownerType === 'individual' ? t('ownerTypeIndividual') : t('ownerTypeCompany');
      case 'alsoDriver':
        return v.ownerAlsoDriver === 'yes'
          ? t('chatAlsoDriverYes')
          : t('chatAlsoDriverNo');
      case 'nif': return v.ownerNif || '';
      case 'companyName': return v.companyName || '';
      case 'companyNipc': return v.companyNipc || '';
      case 'license': return v.numeroLicenca || '';
    }
  };

  if (supportToken) {
    return <SupportAccountRegistration supportToken={supportToken} />;
  }

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Appbar.Header>
        {phase === 'register' && <Appbar.BackAction onPress={() => router.back()} />}
        <Appbar.Content title={t('registerOwnerTitle')} />
      </Appbar.Header>

      {phase === 'register' ? (
        <>
      <View style={styles.progressRow}>
        <IconButton
          icon="arrow-left"
          size={20}
          onPress={goBack}
          accessibilityLabel={t('back')}
          testID="chat-back"
        />
        <View style={styles.progressBarWrap}>
          <ProgressBar
            progress={(stepIndex + 1) / totalSteps}
            color={theme.colors.primary}
            style={styles.progressBar}
          />
        </View>
        <Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant, marginRight: 12 }}>
          {t('chatProgress', { current: stepIndex + 1, total: totalSteps })}
        </Text>
      </View>

      <ChatConversation
        composer={
          <DataSetView
            testID="chat-composer"
            dataSet={{ state: typingBeat ? 'typing' : 'idle' }}
            style={typingBeat ? styles.hiddenComposer : undefined}
            pointerEvents={typingBeat ? 'none' : 'auto'}
            accessibilityElementsHidden={typingBeat}
            importantForAccessibility={typingBeat ? 'no-hide-descendants' : 'auto'}
          >
            {ALL_STEP_IDS.map(id => (
              <View key={id} style={id === stepId ? undefined : styles.hiddenStep}>
                {renderStepInputFor(id)}
              </View>
            ))}
            {stepId !== 'ownerType' && stepId !== 'alsoDriver' && (
              <Button
                mode="contained"
                onPress={handleContinue}
                loading={isSubmitting || checking}
                disabled={isSubmitting || checking || typingBeat}
                style={styles.continueButton}
                contentStyle={styles.continueButtonContent}
                testID="chat-continue"
              >
                {isLastStep ? t('submitButton') : t('chatContinue')}
              </Button>
            )}
          </DataSetView>
        }
      >
        <ChatBubble role="assistant">
          {t('chatOwnerIntro')}
        </ChatBubble>
        {stepOrder.slice(0, stepIndex).map(id => (
          <View key={id}>
            <ChatBubble role="assistant">{questionFor[id].question}</ChatBubble>
            <ChatBubble role="user" testID={`chat-answer-${id}`}>{answerSummaryFor(id)}</ChatBubble>
          </View>
        ))}
        {typingBeat ? (
          <TypingIndicator />
        ) : (
          // Active question: heading semantics + live region so AT announces
          // it on Android/web when it appears (iOS announcement is imperative,
          // handled in the useEffect above).
          <ChatBubble
            role="assistant"
            hint={hint}
            testID={`chat-question-${stepId}`}
            accessibilityRole="header"
            accessibilityLiveRegion="polite"
          >
            {question}
          </ChatBubble>
        )}
      </ChatConversation>
        </>
      ) : (
        // M13 point-2: the onboarding chat continues in place. `leading` replays
        // the registration transcript above the CRM question so the whole funnel
        // reads as one uninterrupted conversation.
        <OwnerOnboardingChatView
          testID="owner-plan-chat"
          leading={
            <>
              <ChatBubble role="assistant">
                {t('chatOwnerIntro')}
              </ChatBubble>
              {stepOrder.map(id => (
                <View key={id}>
                  <ChatBubble role="assistant">{questionFor[id].question}</ChatBubble>
                  <ChatBubble role="user" testID={`chat-answer-${id}`}>{answerSummaryFor(id)}</ChatBubble>
                </View>
              ))}
              <ChatBubble role="assistant">
                {t('chatAccountCreatedBridge')}
              </ChatBubble>
              {/* ALL-23: the account exists; offer the app (web only). */}
              <RegistrationAppStoreLinks style={{ marginVertical: 8 }} />
            </>
          }
        />
      )}
      <Portal>
        <Dialog
          visible={dupDialogVisible}
          onDismiss={() => setDupDialogVisible(false)}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('duplicateIdentityTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">{dupDialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDupDialogVisible(false)} testID="owner-duplicate-action-cancel">
              {t('cancel')}
            </Button>
            {dupDialogActions.map((action) => (
              <Button
                key={action}
                mode={action === 'login' ? 'contained' : 'outlined'}
                onPress={() => handleDuplicateIdentityAction(action)}
                testID={`owner-duplicate-action-${action}`}
              >
                {t(getOwnerRegistrationActionLabelKey(action))}
              </Button>
            ))}
          </Dialog.Actions>
        </Dialog>
        <Dialog
          visible={supportDialogVisible}
          testID="owner-support-dialog"
          onDismiss={() => {
            if (!supportSubmitting) setSupportDialogVisible(false);
          }}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('supportRequestTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium" style={{ marginBottom: 12 }}>
              {t('supportRequestIntro')}
            </Text>
            <TextInput
              mode="outlined"
              label={t('supportRequestEmail')}
              value={supportEmail}
              onChangeText={setSupportEmail}
              testID="owner-support-email"
              disabled={supportSubmitting}
              autoCapitalize="none"
              keyboardType="email-address"
              style={{ marginBottom: 10 }}
            />
            <TextInput
              mode="outlined"
              label={t('supportRequestPhone')}
              value={supportPhone}
              onChangeText={setSupportPhone}
              testID="owner-support-phone"
              disabled={supportSubmitting}
              style={{ marginBottom: 10 }}
            />
            <TextInput
              mode="outlined"
              label={t('supportRequestDescription')}
              value={supportDescription}
              onChangeText={setSupportDescription}
              testID="owner-support-description"
              disabled={supportSubmitting}
              multiline
              numberOfLines={4}
            />
            {!!supportMessage && (
              <Text testID="owner-support-message" style={{ marginTop: 10 }} accessibilityRole="alert">
                {supportMessage}
              </Text>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              onPress={() => setSupportDialogVisible(false)}
              disabled={supportSubmitting}
              testID="owner-support-cancel"
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSupportSubmit}
              loading={supportSubmitting}
              disabled={supportSubmitting}
              testID="owner-support-submit"
            >
              {t('supportRequestSubmit')}
            </Button>
          </Dialog.Actions>
        </Dialog>
        <Dialog
          visible={errorDialogVisible}
          onDismiss={() => setErrorDialogVisible(false)}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{errorDialogTitle}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">{errorDialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setErrorDialogVisible(false)} testID="owner-error-dialog-ok">
              {t('okButton')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  supportAccountContent: {
    padding: 20,
    flexGrow: 1,
    justifyContent: 'center',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContainer: {
    padding: 10,
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  surface: {
    width:
      screenWidth <= 480
        ? '90%'
        : screenWidth <= 768
        ? '60%'
        : '40%',
    maxWidth: 480,
    padding: 20,
    borderRadius: 8,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressBarWrap: {
    flex: 1,
    marginHorizontal: 8,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  question: {
    textAlign: 'center',
    marginBottom: 8,
  },
  hint: {
    textAlign: 'center',
    marginBottom: 16,
  },
  input: { marginBottom: 10 },
  hiddenStep: { display: 'none' },
  hiddenComposer: { display: 'none' },
  continueButton: {
    marginTop: 16,
    borderRadius: 12,
  },
  continueButtonContent: {
    paddingVertical: 6,
  },
  phoneInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  countryCodeButton: {
    marginRight: 10,
    minWidth: 80,
  },
  phoneInput: {
    flex: 1,
  },
  ownerTypeOptions: {
    gap: 12,
  },
  ownerTypeOption: {
    borderRadius: 12,
  },
  ownerTypeOptionContent: {
    paddingVertical: 8,
  },
});
